/**
 * Active Parsing Tasks Component
 * 
 * Displays ongoing and recent parsing tasks with real-time status updates
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Tag,
  Progress,
  Space,
  Typography,
  Button,
  Spin,
  Empty,
  Popconfirm,
  message,
} from 'antd'
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  RocketOutlined,
  StopOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import api from '@/services/api'

const { Text } = Typography

interface ParsingTask {
  task_id: string
  status: string
  crm_id: number
  project_id: number | null
  project_name: string
  main_query: string
  region_code: string
  region_name: string
  progress: number
  current_step: string | null
  error_message: string | null
  estimated_time_remaining: number | null  // seconds
  created_at: string | null
  started_at: string | null
  completed_at: string | null
}

const statusColors: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
}

const statusIcons: Record<string, React.ReactNode> = {
  pending: <LoadingOutlined />,
  running: <LoadingOutlined />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидание',
  running: 'Выполняется',
  completed: 'Завершен',
  failed: 'Ошибка',
}

interface TaskProjectInfo {
  taskId: string
  projectId: number | null
  crmId: number
  projectName: string
}

interface ActiveParsingTasksProps {
  onViewTask?: (info: TaskProjectInfo) => void
}

export default function ActiveParsingTasks({ onViewTask }: ActiveParsingTasksProps) {
  const [tasks, setTasks] = useState<ParsingTask[]>([])
  const [loading, setLoading] = useState(false)
  const [autoRefresh] = useState(true)

  useEffect(() => {
    loadTasks()
  }, [])

  // * Auto-refresh every 5 seconds for running tasks
  useEffect(() => {
    if (!autoRefresh) return

    const hasRunningTasks = tasks.some(
      (task) => task.status === 'pending' || task.status === 'running'
    )

    if (!hasRunningTasks) {
      return
    }

    const interval = setInterval(() => {
      loadTasks(true) // * Silent refresh
    }, 5000)

    return () => clearInterval(interval)
  }, [tasks, autoRefresh])

  const loadTasks = async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true)
      }

      const response = await api.getParsingTasks()
      setTasks(response.tasks || [])
    } catch (error) {
      if (!silent) {
        message.error('Ошибка загрузки задач парсинга')
        console.error(error)
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatTimeRemaining = (seconds: number | null) => {
    if (!seconds || seconds <= 0) return null
    
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    
    if (mins > 0) {
      return `~${mins} мин ${secs} сек`
    }
    return `~${secs} сек`
  }

  const columns: ColumnsType<ParsingTask> = [
    {
      title: 'Проект',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
      width: 200,
      render: (name: string, record: ParsingTask) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            CRM: {record.crm_id}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Параметры',
      key: 'params',
      width: 250,
      render: (_: any, record: ParsingTask) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            <strong>Запрос:</strong> {record.main_query || '—'}
          </Text>
          <Text style={{ fontSize: 12 }}>
            <strong>Регион:</strong> {record.region_name || record.region_code || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => (
        <Tag icon={statusIcons[status]} color={statusColors[status]}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Прогресс',
      key: 'progress',
      width: 200,
      render: (_: any, record: ParsingTask) => {
        if (record.status === 'pending') {
          return <Text type="secondary">Ожидание...</Text>
        }

        if (record.status === 'running') {
          const timeRemaining = formatTimeRemaining(record.estimated_time_remaining)
          
          return (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Progress
                percent={Math.round(record.progress || 0)}
                size="small"
                status="active"
              />
              {record.current_step && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {record.current_step}
                </Text>
              )}
              {timeRemaining && (
                <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                  ⏱️ Осталось: {timeRemaining}
                </Text>
              )}
            </Space>
          )
        }

        if (record.status === 'completed') {
          return <Text type="success">100%</Text>
        }

        if (record.status === 'failed') {
          return (
            <Text type="danger" style={{ fontSize: 11 }}>
              {record.error_message || 'Ошибка'}
            </Text>
          )
        }

        return null
      },
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (
        <Text style={{ fontSize: 12 }}>{formatDate(date)}</Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_: any, record: ParsingTask) => (
        <Space size={4}>
          {record.status === 'completed' && (
            <Button
              type="link"
              size="small"
              onClick={() => onViewTask?.({
                taskId: record.task_id,
                projectId: record.project_id,
                crmId: record.crm_id,
                projectName: record.project_name,
              })}
            >
              Посмотреть
            </Button>
          )}
          {(record.status === 'running' || record.status === 'pending') && (
            <Popconfirm
              title="Отменить парсинг?"
              description="Задача будет помечена как завершённая с ошибкой. Вы сможете запустить новый парсинг."
              onConfirm={async () => {
                try {
                  await api.cancelParsingTask(record.task_id)
                  message.success('Парсинг отменён')
                  loadTasks()
                } catch (error: any) {
                  message.error('Ошибка отмены: ' + (error?.response?.data?.detail || 'Неизвестная ошибка'))
                }
              }}
              okText="Да, отменить"
              cancelText="Нет"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                danger
                size="small"
                icon={<StopOutlined />}
              >
                Отменить
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <Card
      title={
        <Space>
          <RocketOutlined />
          <span>Активные и недавние парсинги</span>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadTasks()}
            loading={loading}
            size="small"
          >
            Обновить
          </Button>
        </Space>
      }
    >
      {loading && tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Загрузка...</Text>
          </div>
        </div>
      ) : tasks.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Нет активных парсингов"
        >
          <Text type="secondary">
            Запустите парсинг из настроек credentials выше
          </Text>
        </Empty>
      ) : (
        <Table
          dataSource={tasks}
          columns={columns}
          loading={loading && tasks.length > 0}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Всего: ${total}`,
          }}
          rowKey="task_id"
          size="small"
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  )
}

